import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Element } from 'react-scroll';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { Button, Input, UncontrolledTooltip } from 'reactstrap';

import { setFileCommentHighlighted } from '../../actions/Files';

import { isLoggedIn } from '../../api/Auth';

import Avatar from '../Avatar';
import AvatarPopover from '../AvatarPopover';
import DateTime from '../DateTime';
import LinkifyExt from '../LinkifyExt';
import CommentsMentionsEditor from './Mentions/CommentsMentionsEditor';
import CommentsMentionsRenderer from './Mentions/CommentsMentionsRenderer';

import { userCan } from '../../helpers/Auth';
import { getFormattedDateTime } from '../../helpers/DateTime';

import 'react-mentions-ts/styles/tailwind.css';

const FileComment = ({
  comment,
  isReply,
  isEdit,
  setScrollTo,
  setParent,
  setEdit,
  inputRefFunc,
  saving,
  deleting,
  highlighted,
  handleKeyPress,
  handleCommentChange,
  commentState,
  handleCommentDurationChange,
  handleDeleteComment,
  highlightComment,
  alias,
  handleSidebarTab,
  readOnly,
}) => {
  const intl = useIntl();

  const handleJumpToComment = (commentHighlighted) => {
    handleSidebarTab('comments');
    highlightComment(commentHighlighted);
  };

  const getJumpCommentType = (type) => {
    if (type === 'pdf') {
      return 'document';
    }
    if (type === 'video') {
      return 'video';
    }
    return 'image';
  };

  return (
    <Element
      id={`comment-${comment.id}${alias ? `-${alias}` : ''}`}
      className={`file-comment${isReply ? ' file-comment-reply' : ''}${highlighted ? ' file-comment-new' : ''}`}
      name={`comment-${comment.id}${alias ? `-${alias}` : ''}`}
    >
      <Avatar
        id={`comment-user-avatar-${comment.id}`}
        user={comment.user}
        icon={comment.user ? 'user' : 'at'}
        expandable={true}
      />
      <div className="comment-body">
        <div className="comment-wrapper">
          {isEdit ? (
            <div className="comment-content">
              <CommentsMentionsEditor
                commentValue={commentState || comment.comment}
                handleCommentChange={handleCommentChange}
                handleKeyPress={handleKeyPress}
                inputRefFunc={inputRefFunc}
                isReply={isReply}
                disabled={saving}
              />
              {(!isReply &&
              (alias === 'video' || comment.type === 'video') && comment.meta.duration) && (
                <>
                  <Input
                    className={
                      classnames('form-control comment-content-duration', { disabled: saving })
                    }
                    name="commentDuration"
                    placeholder={intl.formatMessage({ defaultMessage: 'Duration' })}
                    disabled={saving}
                    onChange={handleCommentDurationChange}
                    defaultValue={comment.meta.duration}
                    onKeyPress={handleKeyPress}
                    size={4}
                    type="number"
                    id="comment-content-duration"
                    min={0.01}
                  />
                  <UncontrolledTooltip
                    fade={false}
                    placement="bottom"
                    target="comment-content-duration"
                  >
                    {intl.formatMessage({ defaultMessage: 'Duration (seconds)' })}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          ) : (
            <p>
              <span className="comment-author">
                {comment.user ? (
                  <span>
                    {isLoggedIn() ? (
                      <>
                        <a id={`comment-user-name-${comment.id}`} href="#profile" onClick={(e) => { e.preventDefault(); }}>
                          {comment.user.name}
                        </a>
                        <AvatarPopover
                          user={comment.user}
                          targetId={`comment-user-name-${comment.id}`}
                        />
                      </>
                    ) : (
                      <span id={`comment-user-name-${comment.id}`}>
                        {comment.user.name}
                      </span>
                    )}
                  </span>
                ) : (
                  <span>{comment.name}</span>
                )}
              </span>
              <LinkifyExt>
                <CommentsMentionsRenderer
                  comment={comment}
                />
              </LinkifyExt>
            </p>
          )}
          {!isEdit && !isReply &&
          (alias === 'pdf' || alias === 'image' || alias === 'video') && (
            <div className="comment-jump-to-sidebar">
              <Button
                color="link"
                id={`CommentJumpToSidebar-${comment.id}`}
                size="sm"
                onClick={() => handleJumpToComment({ commentId: comment.id, type: 'sidebar' })}
              >
                <i className="far fa-square-up-right" />
              </Button>
              <UncontrolledTooltip
                fade={false}
                placement="top"
                target={`CommentJumpToSidebar-${comment.id}`}
              >
                {intl.formatMessage({ defaultMessage: 'Jump to comment in sidebar' })}
              </UncontrolledTooltip>
            </div>
          )}
          {!isEdit && !isReply && !alias &&
          (comment.type === 'pdf' || comment.type === 'image' || comment.type === 'video') && (
            <div className="comment-jump-to-file">
              <Button
                color="link"
                id={`CommentJumpToFile-${comment.id}`}
                size="sm"
                onClick={() => handleJumpToComment({
                  commentId: comment.id,
                  type: getJumpCommentType(comment.type),
                })}
              >
                <i className="far fa-square-up-right" />
              </Button>
              <UncontrolledTooltip
                fade={false}
                placement="top"
                target={`CommentJumpToFile-${comment.id}`}
              >
                {comment.type === 'pdf' &&
                intl.formatMessage({ defaultMessage: 'Jump to comment in document' })}
                {comment.type === 'image' &&
                intl.formatMessage({ defaultMessage: 'Highlight comment in image' })}
                {comment.type === 'video' &&
                intl.formatMessage({ defaultMessage: 'Jump to comment in video' })}
              </UncontrolledTooltip>
            </div>
          )}
        </div>
        <ul className="comment-meta">
          {!readOnly && (
            <>
              <li className="comment-reply">
                <Link
                  to="#reply"
                  onClick={(e) => {
                    e.preventDefault();
                    setParent(comment.parent_id ? comment.parent_id : comment.id);
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'Reply' })}
                </Link>
              </li>
              {userCan('comment.edit', comment) && (
                <li className="comment-edit">
                  {isEdit ? (
                    <Link
                      to="#edit"
                      onClick={(e) => {
                        e.preventDefault();
                        setEdit(null);
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Cancel edit' })}
                    </Link>
                  ) : (
                    <Link
                      to="#edit"
                      onClick={(e) => {
                        e.preventDefault();
                        setEdit(comment.id);
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Edit' })}
                    </Link>
                  )}
                </li>
              )}
              {userCan('comment.delete', comment) && (
                <li className="comment-delete">
                  <Link
                    to="#delete"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteComment(comment.id);
                    }}
                  >
                    {deleting ? intl.formatMessage({ defaultMessage: 'Deleting...' }) :
                      intl.formatMessage({ defaultMessage: 'Delete' })}
                  </Link>
                </li>
              )}
            </>
          )}
          <li className="comment-date">
            <Link
              to={`#comment-${comment.id}`}
              onClick={(e) => { e.preventDefault(); setScrollTo(`comment-${comment.id}`); }}
            >
              <DateTime
                id={`comment-created-${comment.id}`}
                dateTime={comment.created_at}
              />
            </Link>
            {comment.created_at !== comment.updated_at && (
              <span title={`edited: ${getFormattedDateTime(comment.updated_at, true)}`} className="comment-edited">{' '}
                {intl.formatMessage({ defaultMessage: '(edited)' })}
              </span>
            )}
          </li>
        </ul>
      </div>
    </Element>
  );
};

const mapDispatchToProps = dispatch => ({
  highlightComment: (commentHighlighted) => {
    dispatch(setFileCommentHighlighted(commentHighlighted));
  },
});

FileComment.propTypes = {
  comment: PropTypes.object.isRequired,
  isReply: PropTypes.bool,
  isEdit: PropTypes.bool,
  setScrollTo: PropTypes.func.isRequired,
  setParent: PropTypes.func.isRequired,
  setEdit: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  deleting: PropTypes.bool.isRequired,
  highlighted: PropTypes.bool.isRequired,
  inputRefFunc: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  handleCommentChange: PropTypes.func.isRequired,
  commentState: PropTypes.string,
  handleCommentDurationChange: PropTypes.func,
  handleDeleteComment: PropTypes.func.isRequired,
  highlightComment: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  alias: PropTypes.string,
  readOnly: PropTypes.bool,
};

FileComment.defaultProps = {
  isReply: false,
  isEdit: false,
  commentState: '',
  alias: '',
  readOnly: false,
  handleCommentDurationChange: () => {},
};

export default connect(null, mapDispatchToProps)(FileComment);
